package com.esoris.esocomponent.ui.common

import androidx.compose.foundation.layout.padding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp

@Composable
fun EsoText(
    text: String,
    modifier: Modifier = Modifier,
    style: TextStyle? = null,
    color: Color? = null,
    maxLines: Int = Int.MAX_VALUE,
    overflow: TextOverflow = TextOverflow.Ellipsis,
    textAlign: TextAlign? = null,
    softWrap: Boolean = true,
) {
    Text(
        text = text,
        modifier = modifier,
        style = style ?: MaterialTheme.typography.bodyLarge,
        color = color ?: MaterialTheme.colorScheme.onBackground,
        maxLines = maxLines,
        overflow = overflow,
        textAlign = textAlign,
        softWrap = softWrap
    )
}

@Composable
fun EsoTextError(
    errorMessage: String?,
    modifier: Modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp),
    color: Color = MaterialTheme.colorScheme.error,
) {
    if (!errorMessage.isNullOrBlank()) {
        EsoText(
            text = errorMessage,
            modifier = modifier,
            style = MaterialTheme.typography.bodySmall,
            color = color,
            textAlign = TextAlign.Start,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis
        )
    }
}

/**
 * Primary title text — larger, bold, for section headers or screen titles.
 */
@Composable
fun EsoTextTitle(
    text: String,
    modifier: Modifier = Modifier,
    color: Color? = null,
    textAlign: TextAlign? = null,
    maxLines: Int = 1,
    overflow: TextOverflow = TextOverflow.Ellipsis
) {
    EsoText(
        text = text,
        modifier = modifier,
        style = MaterialTheme.typography.titleLarge,
        color = color ?: MaterialTheme.colorScheme.onBackground,
        textAlign = textAlign,
        maxLines = maxLines,
        overflow = overflow
    )
}

/**
 * Secondary text — smaller and softer tone, often used as subtitles or descriptions.
 */
@Composable
fun EsoTextSubTitle(
    text: String,
    modifier: Modifier = Modifier,
    color: Color? = null,
    textAlign: TextAlign? = null,
    maxLines: Int = 1,
    overflow: TextOverflow = TextOverflow.Ellipsis
) {
    EsoText(
        text = text,
        modifier = modifier,
        style = MaterialTheme.typography.bodyMedium,
        color = color ?: MaterialTheme.colorScheme.onSurfaceVariant,
        textAlign = textAlign,
        maxLines = maxLines,
        overflow = overflow
    )
}



@Preview(showBackground = true)
@Composable
private fun EsoTextPreview() {
    EsoText(text = "This is a default EsoText.")
}

@Preview(showBackground = true)
@Composable
private fun EsoTextWithLongTextPreview() {
    EsoText(
        text = "This is a very long EsoText that should overflow with ellipsis at the end.",
        maxLines = 1
    )
}

@Preview(showBackground = true)
@Composable
private fun EsoTextErrorPreview() {
    EsoTextError(errorMessage = "This is an error message.")
}

@Preview(showBackground = true)
@Composable
private fun EsoTextErrorNullPreview() {
    EsoTextError(errorMessage = null)
}

@Preview(showBackground = true)
@Composable
private fun EsoTextTitlePreview() {
    EsoTextTitle(text = "This is a Title")
}

@Preview(showBackground = true)
@Composable
private fun EsoTextTitleLongPreview() {
    EsoTextTitle(
        text = "This is a very long title that will surely be truncated with an ellipsis.",
        maxLines = 1
    )
}

@Preview(showBackground = true)
@Composable
private fun EsoTextSubTitlePreview() {
    EsoTextSubTitle(text = "This is a subtitle, providing more details.")
}

@Preview(showBackground = true)
@Composable
private fun EsoTextSubTitleLongPreview() {
    EsoTextSubTitle(text = "This is a longer subtitle that might wrap or get ellipsized depending on the constraints.")
}

